import { ArrowRight, PartyPopper } from "lucide-react";

const tiers = [
    {
        name: "Small",
        price: "$125",
        starting: "starting at",
        desc: "Minimalist line work, fineline symbols, palm-fitting pieces.",
        features: ["Up to 2 hours", "Black ink", "Single session"],
    },
    {
        name: "Medium",
        price: "$200+",
        starting: "from",
        desc: "Forearm scripts, palm-sized custom designs, moderate detail.",
        features: ["2–4 hour session", "Custom design", "Color or black"],
        highlighted: true,
    },
    {
        name: "Large",
        price: "$350+",
        starting: "from",
        desc: "Half sleeves, complex shading, thigh + back pieces.",
        features: ["4–6 hour sessions", "Multi-session ok", "Detailed shading"],
    },
    {
        name: "Sleeves / Custom",
        price: "Consult",
        starting: "by",
        desc: "Full sleeves, large-scale coverups, multi-session storytelling pieces.",
        features: ["In-person consult", "Custom drafting", "Hourly rate"],
    },
];

const goBooking = () => {
    document.getElementById("booking")?.scrollIntoView({ behavior: "smooth" });
};

export const Services = () => {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-24 md:py-32 bg-black"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <div className="h-px w-10 bg-[#7CFC00]" />
                        <span className="text-[#7CFC00] uppercase tracking-[0.3em] text-xs font-semibold">
                            Pricing
                        </span>
                        <div className="h-px w-10 bg-[#7CFC00]" />
                    </div>
                    <h2
                        data-testid="services-title"
                        className="font-display text-5xl md:text-7xl text-white uppercase tracking-tight leading-none"
                    >
                        Rates <span className="text-[#7CFC00] text-glow">Straight</span> Up.
                    </h2>
                    <p className="mt-5 text-zinc-400 text-base md:text-lg">
                        Transparent pricing — no hidden fees. Consults are free, deposits hold your slot.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                    {tiers.map((t, i) => (
                        <div
                            key={t.name}
                            data-testid={`tier-card-${t.name.toLowerCase().replace(/\s|\//g, "-")}`}
                            className={`relative p-7 md:p-8 border transition-all duration-300 hover:-translate-y-1 ${
                                t.highlighted
                                    ? "bg-[#0c1a04] border-[#7CFC00] glow-neon"
                                    : "bg-[#0a0a0a] border-white/10 hover:border-[#7CFC00]"
                            }`}
                        >
                            {t.highlighted && (
                                <div className="absolute -top-3 left-7 bg-[#7CFC00] text-black text-[10px] uppercase tracking-widest font-bold px-3 py-1">
                                    Most Booked
                                </div>
                            )}
                            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">
                                {t.starting}
                            </div>
                            <div className="mt-2 flex items-baseline gap-2">
                                <span className="font-display text-5xl md:text-6xl text-white">{t.price}</span>
                            </div>
                            <div className="mt-3 font-display text-2xl tracking-wide text-[#7CFC00] uppercase">
                                {t.name}
                            </div>
                            <p className="mt-3 text-zinc-400 text-sm leading-relaxed">{t.desc}</p>

                            <ul className="mt-5 space-y-2 border-t border-white/10 pt-4">
                                {t.features.map((f, idx) => (
                                    <li key={idx} className="text-sm text-zinc-300 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-[#7CFC00]" />
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <button
                                data-testid={`tier-book-${i}`}
                                onClick={goBooking}
                                className={`mt-6 w-full uppercase tracking-widest text-xs font-bold py-3 flex items-center justify-center gap-2 transition-all ${
                                    t.highlighted
                                        ? "bg-[#7CFC00] text-black hover:bg-[#65E000]"
                                        : "bg-transparent text-white border border-white/20 hover:border-[#7CFC00] hover:text-[#7CFC00]"
                                }`}
                            >
                                Book This <ArrowRight size={14} />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Tattoo Parties banner */}
                <div
                    data-testid="parties-banner"
                    className="mt-10 relative overflow-hidden border border-[#7CFC00]/40 bg-gradient-to-r from-[#0a0a0a] via-[#0c1a04] to-[#0a0a0a] p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between"
                >
                    <div className="absolute -right-10 -bottom-10 opacity-10 pointer-events-none">
                        <PartyPopper size={220} className="text-[#7CFC00]" />
                    </div>
                    <div className="relative">
                        <div className="text-[#7CFC00] uppercase tracking-[0.3em] text-xs font-semibold mb-3">
                            New
                        </div>
                        <div className="font-display text-3xl md:text-5xl text-white uppercase">
                            Tattoo Parties <span className="text-[#7CFC00]">Available</span>
                        </div>
                        <p className="mt-3 text-zinc-300 max-w-2xl">
                            Birthdays, bachelorette weekends, crew nights — book the studio for your group. Custom
                            flash, group rates, the whole vibe.
                        </p>
                    </div>
                    <button
                        data-testid="parties-cta"
                        onClick={goBooking}
                        className="relative bg-[#7CFC00] text-black uppercase font-bold tracking-widest text-sm px-7 py-4 hover:bg-[#65E000] transition-colors glow-neon flex items-center gap-2"
                    >
                        Plan Your Party <ArrowRight size={16} />
                    </button>
                </div>
            </div>
        </section>
    );
};
